import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {useInvalidateChatNeedsReply} from "@/api/chatApi";
import {SUBMISSION_KEYS} from "@/api/submissionApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import MarkdownEditor from "@/components/Common/MarkdownEditor";
import Modal from "@/components/Common/Modal";
import ContentApprovalMedia from "@/components/ContentApproval/ContentApprovalMedia";
import ContentGuidanceBox from "@/components/ContentApproval/ContentGuidanceBox";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import {useManagementCampaign} from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import {rejectContentModalAtom} from "@/store/jotaiAtoms";
import type {TContentSubmission} from "@/store/types";
import {getCarouselItemFromSubmission} from "@/utils/campaignUtils";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDraggable} from "react-use-draggable-scroll";

const RejectContentModal: FC = () => {
  const {t} = useTranslation(["content_approval", "common"]);
  const campaign = useManagementCampaign();
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const openCarousel = useCarouselStore(state => state.open);
  const setCarouselValue = useCarouselStore(state => state.setValue);
  const [{submission, submissionId, user}, setData] = useAtom(rejectContentModalAtom);
  const [reason, setReason] = useState<string>();
  const queryClient = useQueryClient();
  const invalidateChatNeedsReply = useInvalidateChatNeedsReply();
  const divRef = useRef<HTMLDivElement>(null!);
  const {events} = useDraggable(divRef);

  const {data: submissionData} = useGenericQuery<TContentSubmission>({
    queryKey: SUBMISSION_KEYS.qSubmission(submissionId!),
    enabled: !!submissionId && !submission,
  });

  useEffect(() => {
    if (submissionData) {
      setData(state => {
        state.submission = submissionData;
      });
    }
  }, [submissionData]);

  const {mutate: rejectContent, isPending} = useMutation({
    mutationKey: SUBMISSION_KEYS.mRejectSubmission(submission?.id!),
    mutationFn: (feedback: string) =>
      client.post(SUBMISSION_KEYS.mRejectSubmission(submission?.id!)[0], {feedback_message: feedback}),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, search),
        CAMPAIGN_KEYS.qCampaignManagement(campaign.id),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      invalidateChatNeedsReply();

      capturePostHogEvent(PostHogEvents.ContentRejected, {campaignId: campaign.id, submissionId: submission?.id});

      onClose();
    },
  });

  const onClose = () => {
    setData(state => {
      state.isOpen = false;
      state.submission = null;
      state.submissionId = null;
      state.user = null;
    });
  };

  if (!submission || !user) {
    return <div ref={divRef} className="hidden" />;
  }

  return (
    <Modal
      backgroundClassName="max-w-130"
      confirmButtonStyle="Danger"
      confirmButtonText={t("reject_content")}
      isConfirmDisabled={!reason}
      isLoading={isPending}
      modalTitle={t("reject_content")}
      onClose={onClose}
      onConfirm={() => {
        if (reason) {
          rejectContent(reason);
        }
      }}
      shouldCloseOnConfirm={false}>
      <div className="bg-warning-light-yellow flex gap-2 rounded-md p-3">
        <WarningIcon className="text-warning-mustard size-4 shrink-0" />
        <span className="text-neutral-black text-xs">{t("reject_content_warning")}</span>
      </div>
      <div className="flex flex-col gap-3">
        {user && (
          <div ref={divRef} {...events} className="flex shrink-0 gap-3 overflow-x-auto">
            {(submission.carousel_medias?.length ? submission.carousel_medias : [submission]).map((media, index) => (
              <ContentApprovalMedia
                key={index}
                media={media}
                onClick={() => {
                  setCarouselValue("defaultAlbumItem", {...media, media_type: media.media_type ?? "IMAGE"});

                  openCarousel({
                    campaign: {id: campaign.id, title: campaign.title!},
                    media: [
                      getCarouselItemFromSubmission({
                        isInstagram: !!campaign.instagram,
                        submission,
                        user,
                      }),
                    ],
                    showCaption: false,
                    showFooter: false,
                    variant: "content_approval",
                  });
                }}
              />
            ))}
          </div>
        )}
        <ContentGuidanceBox
          className="min-h-none max-h-none gap-3 rounded-xl border border-none p-3"
          description={submission.caption}
          descriptionClassName="p-0 rounded-xl "
          influencerImage={user?.picture}
          title={t("caption")}
          titleClassName="min-h-fit p-0 text-neutral-black rounded-xl"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-neutral-black text-xs">{t("reason_for_rejecting")}</span>
        <MarkdownEditor
          autoSize
          className="md-editor-text-xs border-neutral-white-dark divide-neutral-white-dark max-h-60 border shadow-none"
          placeholder={t("reason_for_rejecting_placeholder")}
          onChange={setReason}
          value={reason ?? ""}
          visibleDragbar={false}
        />
      </div>
    </Modal>
  );
};

export default RejectContentModal;
